import { useState } from "react";
import { BookOpen, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { imgUrl } from "@/lib/api";
import { Sparkle, SectionTitle, Reveal } from "./Decor";

export const LibrarySection = ({ c, s }) => {
  const items = (c?.articles || []).filter((a) => a.title && a.published !== false);
  const [open, setOpen] = useState(null);
  return (
    <section id="library" className="relative overflow-hidden bg-gradient-to-b from-purple-50 to-white py-16 sm:py-20" data-testid="library-section">
      <Sparkle className="absolute right-[8%] top-12 h-6 w-6 text-yellow-300" />
      <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
        <SectionTitle id="library" title={s.title} subtitle={s.subtitle} />
        {items.length === 0 ? (
          <Reveal className="flex items-center gap-4 rounded-[2rem] bg-white p-6 ring-1 ring-purple-100">
            <BookOpen className="h-8 w-8 shrink-0 text-purple-400" />
            <p className="text-slate-600" data-testid="library-empty">{c?.settings?.library_empty_text}</p>
          </Reveal>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((a, i) => (
              <Reveal key={a.id || i} delay={(i % 3) * 0.08}>
                <button onClick={() => setOpen(a)} data-testid={`article-card-${i}`} className="group flex h-full w-full flex-col overflow-hidden rounded-[2rem] bg-white text-left shadow-sm ring-1 ring-purple-100 transition-shadow duration-300 hover:shadow-lg">
                  {a.image && (
                    <div className="h-48 overflow-hidden">
                      <img src={imgUrl(a.image)} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-5">
                    <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
                      {a.category && <span className="rounded-full bg-purple-100 px-3 py-1 text-purple-700">{a.category}</span>}
                      {a.date && <span className="text-slate-500">{a.date}</span>}
                    </div>
                    <h3 className="font-display mt-3 text-lg font-semibold leading-snug text-blue-900">{a.title}</h3>
                    {a.summary && <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-slate-600">{a.summary}</p>}
                    <span className="mt-auto inline-flex items-center gap-1.5 pt-4 text-sm font-semibold text-pink-600">
                      Read more <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </span>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        )}
      </div>
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto rounded-3xl p-0" data-lenis-prevent data-testid="article-dialog">
          {open?.image && <img src={imgUrl(open.image)} alt={open?.title} className="h-56 w-full object-cover sm:h-72" />}
          <div className="p-6 sm:p-8">
            {open?.category && <span className="rounded-full bg-purple-100 px-3 py-1 text-xs font-semibold text-purple-700">{open.category}</span>}
            <DialogTitle className="font-display mt-3 text-2xl font-semibold text-blue-900 sm:text-3xl">{open?.title}</DialogTitle>
            {open?.date && <p className="mt-1 text-sm text-slate-500">{open.date}</p>}
            <div className="mt-5 whitespace-pre-line text-base leading-relaxed text-slate-700">{open?.content || open?.summary}</div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};
